/**
 * UploadProgress Component
 *
 * Displays upload progress with a progress bar, file stats, and control buttons.
 * Supports pause, resume, cancel, and retry actions.
 */

import { Pause, Play, X, AlertCircle, CheckCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { UploadState } from "@/hooks/useAudioUpload";

interface UploadProgressProps {
  state: UploadState;
  filename: string;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  className?: string;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format seconds to human-readable time string
 */
function formatEta(seconds: number | null): string {
  if (seconds === null) return "--:--";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export function UploadProgress({
  state,
  filename,
  onPause,
  onResume,
  onCancel,
  onRetry,
  className,
}: UploadProgressProps) {
  const isUploading = state.status === "uploading";
  const isPaused = state.status === "paused";
  const isError = state.status === "error";
  const isCompleted = state.status === "completed";

  return (
    <div className={cn("rounded-lg border bg-card p-4", className)}>
      {/* Filename and status */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium truncate max-w-[200px]">
          {filename}
        </span>
        <div className="flex items-center gap-2">
          {isCompleted && <CheckCircle className="h-4 w-4 text-green-500" />}
          {isError && <AlertCircle className="h-4 w-4 text-destructive" />}
        </div>
      </div>

      {/* Progress bar */}
      <Progress value={state.progress} className="h-2 mb-2" />

      {/* Stats and controls */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>{state.progress.toFixed(1)}%</span>
          {state.totalParts > 1 && (
            <span>
              Part {state.currentPart}/{state.totalParts}
            </span>
          )}
          <span>
            {formatBytes(state.bytesUploaded)} / {formatBytes(state.totalBytes)}
          </span>
          {(isUploading || isPaused) && (
            <span>ETA: {formatEta(state.eta)}</span>
          )}
        </div>

        {/* Control buttons */}
        <div className="flex items-center gap-1">
          {isUploading && onPause && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onPause}
              title="Pause"
            >
              <Pause className="h-3 w-3" />
            </Button>
          )}
          {isPaused && onResume && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onResume}
              title="Resume"
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
          {isError && onRetry && (
            <Button variant="ghost" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
          {(isUploading || isPaused) && onCancel && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onCancel}
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Error message */}
      {isError && state.error && (
        <p className="text-xs text-destructive mt-2">{state.error}</p>
      )}
    </div>
  );
}
